import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildClientPageReportQuery,
  buildSocialPerformanceFetchKey,
  buildPulseSocialPerformanceFetchKey,
  formatSocialPerformanceDate,
  normalizeSocialPerformancePayload,
  parseSocialPerformanceDateParts,
  tableSourceToAggregationMode,
} from "../../lib/utils/socialPerformanceReport"
import { skipIfFetchKeyLoaded, skipIfPulseFetchKeyLoaded } from "../utils/fetchDedupe"

function socialPerformanceArgKey(arg = {}) {
  const { clientId, startDate, endDate, source, rangeMode } = arg
  return buildSocialPerformanceFetchKey(
    clientId,
    formatSocialPerformanceDate(startDate),
    formatSocialPerformanceDate(endDate),
    tableSourceToAggregationMode(source),
    rangeMode,
  )
}

/**
 * GET /api/v1/report/clients/{clientId}/page
 * Month: ?aggregation_mode=&period=month&year=&month=
 * Custom range: ?aggregation_mode=&start_date=&end_date=
 */
export const fetchClientSocialPerformance = createAsyncThunk(
  "clientSocialPerformance/fetch",
  async ({ clientId, startDate, endDate, source, rangeMode } = {}, { rejectWithValue, getState }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }

      const start = formatSocialPerformanceDate(startDate)
      const end = formatSocialPerformanceDate(endDate)
      const parts = parseSocialPerformanceDateParts(end || start)
      if (!start || !end || (rangeMode !== "custom" && !parts)) {
        return rejectWithValue("startDate and endDate are required")
      }
      const aggregationMode = tableSourceToAggregationMode(source)
      const fetchKey = buildSocialPerformanceFetchKey(
        clientId,
        start,
        end,
        aggregationMode,
        rangeMode,
      )
      const cached = getState().clientSocialPerformance?.byFetchKey?.[fetchKey]
      if (cached) {
        return {
          raw: cached.raw,
          platforms: cached.platforms,
          clientId: cached.clientId ?? String(clientId),
          startDate: cached.startDate ?? start,
          endDate: cached.endDate ?? end,
          fetchKey,
        }
      }
      const query = buildClientPageReportQuery(
        rangeMode === "custom"
          ? {
              period: "custom",
              startDate: start,
              endDate: end,
              aggregationMode,
            }
          : {
              period: "month",
              year: parts.year,
              month: parts.month,
              aggregationMode,
            },
      )
      const collected = []
      let endpoint = `report/clients/${clientId}/page?${query}`
      let raw = null
      for (let page = 0; page < 20; page += 1) {
        const data = await get(endpoint)
        if (page === 0) raw = data
        const rows = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.pages)
            ? data.pages
            : Array.isArray(data)
              ? data
              : []
        collected.push(...rows)
        const next = data?.paging?.next
        if (!next) break
        endpoint = next
      }
      const payload = collected.length ? { ...(raw ?? {}), data: collected } : raw
      const socialNetworkTypes = getState().socialNetworkTypes?.types ?? []

      return {
        raw: payload,
        platforms: normalizeSocialPerformancePayload(payload, socialNetworkTypes, {
          startDate: start,
          endDate: end,
          aggregationMode,
        }),
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey,
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch social performance")
    }
  },
  {
    condition: (arg, { getState }) => {
      const key = socialPerformanceArgKey(arg)
      const slice = getState().clientSocialPerformance
      if (arg?.prefetch) {
        return !slice.byFetchKey?.[key]
      }
      if (slice.byFetchKey?.[key] && String(slice.fetchedFetchKey ?? "") !== key) {
        return true
      }
      return !skipIfFetchKeyLoaded(slice, key)
    },
  },
)

/**
 * Soho Pulse engagement/followers — daily page report.
 * GET /api/v1/report/clients/{clientId}/page?aggregation_mode=paid_and_organic&period=day&year=&month=&day=
 */
export const fetchClientPulseSocialPerformance = createAsyncThunk(
  "clientSocialPerformance/fetchPulse",
  async ({ clientId, startDate } = {}, { rejectWithValue, getState }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (!startDate) {
        return rejectWithValue("startDate is required")
      }

      const start = formatSocialPerformanceDate(startDate)
      const parts = parseSocialPerformanceDateParts(start)
      if (!parts) {
        return rejectWithValue("startDate is required")
      }
      const query = buildClientPageReportQuery({
        period: "day",
        year: parts.year,
        month: parts.month,
        day: parts.day,
      })
      const data = await get(`report/clients/${clientId}/page?${query}`)
      const socialNetworkTypes = getState().socialNetworkTypes?.types ?? []

      return {
        raw: data,
        platforms: normalizeSocialPerformancePayload(data, socialNetworkTypes, {
          startDate: start,
          endDate: start,
        }),
        clientId: String(clientId),
        startDate: start,
        endDate: start,
        fetchKey: buildPulseSocialPerformanceFetchKey(clientId, start),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch pulse social performance")
    }
  },
  {
    condition: (arg, { getState }) => {
      const { clientId, startDate } = arg || {}
      const start = formatSocialPerformanceDate(startDate)
      const key = buildPulseSocialPerformanceFetchKey(clientId, start)
      const slice = getState().clientSocialPerformance
      if (
        slice.pulseStatus === "succeeded" &&
        String(slice.pulseFetchedFetchKey ?? "") === key &&
        !slice.pulseRaw &&
        !(slice.pulsePlatforms?.length)
      ) {
        return true
      }
      return !skipIfPulseFetchKeyLoaded(slice, key)
    },
  },
)

const initialState = {
  platforms: [],
  raw: null,
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
  pulsePlatforms: [],
  pulseRaw: null,
  pulseStatus: "idle",
  pulseError: null,
  pulseRequestedFetchKey: null,
  pulseFetchedFetchKey: null,
  pulseClientId: null,
  pulseStartDate: null,
  pulseEndDate: null,
  byFetchKey: {},
}

function beginFetch(state, action) {
  if (Boolean(action.meta.arg?.prefetch)) return
  const nextKey = socialPerformanceArgKey(action.meta.arg)
  const cached = state.byFetchKey?.[nextKey]
  state.error = null
  state.requestedFetchKey = nextKey

  if (cached) {
    state.status = "succeeded"
    state.raw = cached.raw
    state.platforms = cached.platforms
    state.clientId = cached.clientId ?? null
    state.startDate = cached.startDate ?? null
    state.endDate = cached.endDate ?? null
    state.fetchedFetchKey = nextKey
    return
  }

  const keepStalePlatforms =
    nextKey === state.fetchedFetchKey &&
    Array.isArray(state.platforms) &&
    state.platforms.length > 0

  state.status = "loading"
  if (!keepStalePlatforms) {
    state.fetchedFetchKey = null
    state.platforms = []
    state.raw = null
  }
}

function isStaleFetch(action, state) {
  const key = socialPerformanceArgKey(action.meta.arg)
  return key !== String(state.requestedFetchKey ?? "")
}

function beginPulseFetch(state, action) {
  const { clientId, startDate } = action.meta.arg || {}
  const start = formatSocialPerformanceDate(startDate)
  const nextKey = buildPulseSocialPerformanceFetchKey(clientId, start)
  const keepStale =
    nextKey === state.pulseFetchedFetchKey &&
    Array.isArray(state.pulsePlatforms) &&
    state.pulsePlatforms.length > 0

  state.pulseStatus = "loading"
  state.pulseError = null
  state.pulseRequestedFetchKey = nextKey
  if (!keepStale) {
    state.pulseFetchedFetchKey = null
    state.pulsePlatforms = []
    state.pulseRaw = null
  }
}

function isStalePulseFetch(action, state) {
  const { clientId, startDate } = action.meta.arg || {}
  const start = formatSocialPerformanceDate(startDate)
  const key = buildPulseSocialPerformanceFetchKey(clientId, start)
  return key !== String(state.pulseRequestedFetchKey ?? "")
}

const clientSocialPerformanceSlice = createSlice({
  name: "clientSocialPerformance",
  initialState,
  reducers: {
    clearClientSocialPerformance: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientSocialPerformance.pending, (state, action) => {
        beginFetch(state, action)
      })
      .addCase(fetchClientSocialPerformance.fulfilled, (state, action) => {
        const fetchKey = action.payload?.fetchKey ?? socialPerformanceArgKey(action.meta.arg || {})
        const raw = action.payload?.raw ?? null
        const platforms = action.payload?.platforms ?? []
        if (fetchKey) {
          state.byFetchKey[fetchKey] = {
            raw,
            platforms,
            clientId: action.payload?.clientId ?? null,
            startDate: action.payload?.startDate ?? null,
            endDate: action.payload?.endDate ?? null,
          }
        }
        if (action.meta.arg?.prefetch || isStaleFetch(action, state)) return
        state.status = "succeeded"
        state.raw = raw
        state.platforms = platforms
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = fetchKey
      })
      .addCase(fetchClientSocialPerformance.rejected, (state, action) => {
        const fetchKey = socialPerformanceArgKey(action.meta.arg || {})
        if (action.meta.arg?.prefetch) {
          if (fetchKey && !state.byFetchKey[fetchKey]) {
            state.byFetchKey[fetchKey] = {
              raw: null,
              platforms: [],
              clientId: null,
              startDate: null,
              endDate: null,
            }
          }
          return
        }
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch social performance"
        state.platforms = []
        state.raw = null
        state.fetchedFetchKey = state.requestedFetchKey
      })
      .addCase(fetchClientPulseSocialPerformance.pending, (state, action) => {
        beginPulseFetch(state, action)
      })
      .addCase(fetchClientPulseSocialPerformance.fulfilled, (state, action) => {
        if (isStalePulseFetch(action, state)) return
        state.pulseStatus = "succeeded"
        state.pulseRaw = action.payload?.raw ?? null
        state.pulsePlatforms = action.payload?.platforms ?? []
        state.pulseClientId = action.payload?.clientId ?? null
        state.pulseStartDate = action.payload?.startDate ?? null
        state.pulseEndDate = action.payload?.endDate ?? null
        state.pulseFetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientPulseSocialPerformance.rejected, (state, action) => {
        if (isStalePulseFetch(action, state)) return
        state.pulseStatus = "failed"
        state.pulseError = action.payload ?? "Failed to fetch pulse social performance"
        state.pulsePlatforms = []
        state.pulseRaw = null
        state.pulseFetchedFetchKey = state.pulseRequestedFetchKey
      })
  },
})

export const { clearClientSocialPerformance } = clientSocialPerformanceSlice.actions
export default clientSocialPerformanceSlice.reducer
